import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_STATUSES = [
    { v: "a_venir", l: "À venir" },
    { v: "en_cours", l: "En cours" },
    { v: "termine", l: "Terminé" },
];

export const AdminProjects = () => {
    const [projects, setProjects] = useState([]);
    const [form, setForm] = useState({ client_email: "", title: "", address: "" });
    const [busy, setBusy] = useState(false);

    const load = () =>
        axios.get(`${API}/admin/projects`).then((r) => setProjects(r.data.projects || [])).catch(() => {});

    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        if (!form.client_email || !form.title) return;
        setBusy(true);
        try {
            await axios.post(`${API}/admin/projects`, form);
            setForm({ client_email: "", title: "", address: "" });
            toast.success("Chantier créé");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur de création");
        } finally {
            setBusy(false);
        }
    };

    const setPhase = async (project, idx, status) => {
        const phases = project.phases.map((p, i) => (i === idx ? { ...p, status } : p));
        try {
            await axios.patch(`${API}/admin/projects/${project.id}`, { phases });
            load();
        } catch {
            toast.error("Erreur de mise à jour");
        }
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer ce chantier ?")) return;
        try {
            await axios.delete(`${API}/admin/projects/${id}`);
            toast.success("Chantier supprimé");
            load();
        } catch {
            toast.error("Erreur");
        }
    };

    return (
        <div className="space-y-6">
            <form onSubmit={create} className="lux-card p-6 grid grid-cols-1 md:grid-cols-4 gap-3">
                <input
                    data-testid="project-client-email"
                    className="lux-input"
                    type="email"
                    placeholder="Email du client *"
                    value={form.client_email}
                    onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                    required
                />
                <input
                    data-testid="project-title"
                    className="lux-input"
                    placeholder="Titre du chantier *"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    required
                />
                <input
                    data-testid="project-address"
                    className="lux-input"
                    placeholder="Adresse"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
                <button data-testid="project-create" type="submit" disabled={busy} className="btn-champagne justify-center disabled:opacity-50">
                    Créer le chantier
                </button>
            </form>

            {projects.length === 0 && (
                <p className="text-[13px] text-[#1C1E1D]/50 italic">Aucun chantier. Créez-en un ci-dessus pour le suivi client.</p>
            )}

            {projects.map((p) => (
                <ProjectCard key={p.id} project={p} onPhase={setPhase} onDelete={del} onChanged={load} />
            ))}
        </div>
    );
};

function ProjectCard({ project, onPhase, onDelete, onChanged }) {
    const [note, setNote] = useState("");
    const fileRef = useRef(null);

    const addNote = async () => {
        if (!note.trim()) return;
        try {
            await axios.post(`${API}/admin/projects/${project.id}/notes`, { text: note.trim() });
            setNote("");
            toast.success("Note ajoutée");
            onChanged();
        } catch {
            toast.error("Erreur");
        }
    };

    const addPhoto = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (f.size > 3 * 1024 * 1024) { toast.error("Photo trop lourde (max 3 Mo)"); return; }
        const b64 = await new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res(r.result.split(",")[1]);
            r.readAsDataURL(f);
        });
        try {
            await axios.post(`${API}/admin/projects/${project.id}/photos`, {
                image_base64: b64, mime_type: f.type, caption: null,
            });
            toast.success("Photo ajoutée");
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur d'envoi");
        }
    };

    const delPhoto = async (pid) => {
        await axios.delete(`${API}/admin/projects/${project.id}/photos/${pid}`).catch(() => {});
        onChanged();
    };

    return (
        <div data-testid={`project-card-${project.id}`} className="lux-card p-6">
            <div className="flex justify-between items-start flex-wrap gap-3 mb-5">
                <div>
                    <div className="font-serif-lux text-xl">{project.title}</div>
                    <div className="text-[13px] text-[#1C1E1D]/60 mt-1">
                        {project.client_email} {project.address ? `· ${project.address}` : ""}
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <div className="text-right">
                        <div className="tracking-luxe text-[#B89C6A] text-[10px]">Avancement</div>
                        <div className="font-serif-lux text-2xl">{project.progress_pct || 0}%</div>
                    </div>
                    <button data-testid="project-delete" onClick={() => onDelete(project.id)} className="text-[11px] tracking-wider uppercase text-red-700/70 hover:text-red-700 underline">
                        Supprimer
                    </button>
                </div>
            </div>

            <div className="h-1 bg-[#1C1E1D]/10 mb-6">
                <div className="h-1 bg-[#B89C6A] transition-all" style={{ width: `${project.progress_pct || 0}%` }} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-6">
                {(project.phases || []).map((ph, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 border hairline px-3 py-2">
                        <span className="text-[12px]">{ph.name}</span>
                        <select
                            data-testid={`phase-select-${i}`}
                            className="lux-input text-[11px] py-1 w-auto"
                            value={ph.status}
                            onChange={(e) => onPhase(project, i, e.target.value)}
                        >
                            {PHASE_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                        </select>
                    </div>
                ))}
            </div>

            {(project.photos || []).length > 0 && (
                <div className="flex flex-wrap gap-3 mb-5">
                    {project.photos.map((ph) => (
                        <div key={ph.id} className="relative group">
                            <img
                                src={`data:${ph.mime_type};base64,${ph.image_base64}`}
                                alt={ph.caption || "Photo chantier"}
                                className="h-24 w-24 object-cover border hairline"
                            />
                            <button
                                onClick={() => delPhoto(ph.id)}
                                className="absolute top-0 right-0 bg-[#1C1E1D]/80 text-white text-[10px] px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {(project.notes || []).length > 0 && (
                <div className="space-y-1 mb-5">
                    {project.notes.map((n) => (
                        <div key={n.id} className="text-[12px] text-[#1C1E1D]/70 border-l-2 border-[#B89C6A] pl-3">
                            {n.text} <span className="text-[#B89C6A]">· {n.created_at?.slice(0, 10)}</span>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex flex-wrap gap-2 pt-4 border-t hairline">
                <input
                    data-testid="project-note-input"
                    className="lux-input flex-1 min-w-[200px]"
                    placeholder="Note d'avancement pour le client…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                />
                <button data-testid="project-note-add" onClick={addNote} className="btn-outline">Ajouter la note</button>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={addPhoto} />
                <button data-testid="project-photo-add" onClick={() => fileRef.current?.click()} className="btn-outline">+ Photo</button>
            </div>
        </div>
    );
}
